import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Bot, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Known resource names to link
const RESOURCE_NAMES = [
  "180 Degrees", "RS EDEN", "Volunteers of America", "Catholic Charities", "Hearth Connection",
  "Simpson Housing", "Damascus Way", "Central Minnesota Reentry Project", "Wayside Recovery",
  "RADIAS Health", "Legal Rights Center", "Mid-Minnesota Legal Aid", "Volunteer Lawyers Network",
  "Southern Minnesota Regional Legal Services", "Attorney General Clean Slate", "HelpSealMyRecord",
  "HIRED", "Twin Cities RISE", "EMERGE", "Amicus", "Minnesota CareerForce", "Goodwill-Easter Seals",
  "T.O.N.E. U.P.", "Ujamaa Place", "Minnesota DOC", "Ramsey County Workforce",
  "People Incorporated", "Hennepin Healthcare", "NorthPoint Health", "Hazelden Betty Ford",
  "EMERGE Trauma Recovery", "Summit Academy", "Minneapolis Adult Education", "Saint Paul College",
  "Literacy Minnesota", "Avivo", "Project for Pride in Living", "PPL",
  "Second Harvest Heartland", "Loaves and Fishes", "Salvation Army", "Hennepin County SNAP",
  "Keystone Community Services", "Neighborhood House", "Sharing & Caring Hands",
  "MACV", "Union Gospel Mission", "American Indian OIC", "Ombishkaa",
  "Breaking Free", "Clare Housing", "Crossing Home", "Next Chapter",
  "Crisis Text Line", "988", "Recovery Community Network", "Minnesota Recovery Connection",
  "United Way 211", "211", "Small Sums", "Metro Transit", "Dress for Success", "Bridging",
  "Diversion Solutions", "Assurance Wireless", "Hennepin County Reentry Court",
  "Marty Mann House", "ANEW", "Solid Ground", "Ramsey County Emergency", "Hennepin County Emergency"
];

const ChatBot = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => {
    const saved = localStorage.getItem("reentry-chat-session");
    return saved || uuidv4();
  });
  
  const scrollAreaRef = useRef(null);
  const inputRef = useRef(null);

  // Function to parse message and create linked content
  const parseMessageWithLinks = (content, closeChat) => {
    let parts = [];
    let lastIndex = 0;
    let matches = [];

    // Find all resource name matches
    RESOURCE_NAMES.forEach(name => {
      const regex = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      let match;
      while ((match = regex.exec(content)) !== null) {
        matches.push({ name, index: match.index, length: match[0].length, matched: match[0] });
      }
    });

    // Sort matches by index
    matches.sort((a, b) => a.index - b.index);

    // Remove overlapping matches
    const filteredMatches = [];
    matches.forEach(match => {
      const lastMatch = filteredMatches[filteredMatches.length - 1];
      if (!lastMatch || match.index >= lastMatch.index + lastMatch.length) {
        filteredMatches.push(match);
      }
    });

    // Build parts array
    filteredMatches.forEach((match, i) => {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
      }
      parts.push({ type: 'link', content: match.matched, searchTerm: match.name });
      lastIndex = match.index + match.length;
    });

    if (lastIndex < content.length) {
      parts.push({ type: 'text', content: content.slice(lastIndex) });
    }

    if (parts.length === 0) {
      return <span>{content}</span>;
    }

    return parts.map((part, i) => {
      if (part.type === 'link') {
        return (
          <button
            key={i}
            onClick={() => {
              closeChat();
              navigate(`/resources?search=${encodeURIComponent(part.searchTerm)}`);
            }}
            className="text-[#0284C7] font-medium underline hover:text-[#0369a1] cursor-pointer"
          >
            {part.content}
          </button>
        );
      }
      return <span key={i}>{part.content}</span>;
    });
  };

  // Initialize with greeting based on language
  useEffect(() => {
    setMessages([{
      role: "assistant",
      content: t('chat.greeting')
    }]);
  }, [i18n.language, t]);

  useEffect(() => {
    localStorage.setItem("reentry-chat-session", sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { role: "user", content: inputValue.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage.content,
        session_id: sessionId,
        history: messages.slice(-10),
        language: i18n.language
      });

      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.response
      }]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: t('chat.error')
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 w-14 h-14 bg-[#0284C7] hover:bg-[#0369a1] text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-[9999]"
          data-testid="chat-toggle-btn"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 w-[380px] h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col z-[9999] chatbot-container animate-slide-up overflow-hidden"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-[#1B3B5A] px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0284C7] rounded-xl flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">{t('chat.title')}</h3>
                <p className="text-white/70 text-xs">{t('chat.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition-colors p-1"
              data-testid="chat-close-btn"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    message.role === "user" 
                      ? "bg-[#0284C7]" 
                      : "bg-slate-100"
                  }`}>
                    {message.role === "user" 
                      ? <User className="w-4 h-4 text-white" />
                      : <Bot className="w-4 h-4 text-slate-600" />
                    }
                  </div>
                  <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    message.role === "user"
                      ? "bg-[#0284C7] text-white rounded-br-sm"
                      : "bg-slate-100 text-slate-700 rounded-bl-sm"
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {message.content}
                    </p>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-slate-600" />
                  </div>
                  <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                      <span className="text-sm text-slate-500">{t('chat.thinking')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('chat.placeholder')}
                className="flex-1 border-slate-200 focus:ring-2 focus:ring-[#0284C7]"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="bg-[#0284C7] hover:bg-[#0369a1] text-white px-4"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2 text-center">
              {t('chat.disclaimer')}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
